import {
  base_colorContentBorder,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_2xl,
  font_family_weight_medium,
  font_family_weight_regular,
  font_sm,
  fontBold,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

import { flyoutSurface, paddingHorizontal } from '../billPayFlyoutChrome.style'

/*
 * Chrome shared with AddVendorFlyout (surface override, header, title, footer)
 * lives in ../billPayFlyoutChrome.style — imported and re-exported below so
 * both Bill Pay flyouts stay identical structurally rather than by comment.
 * Only this flyout's own blocks are defined here.
 */
export {
  flyoutTitle,
  footer,
  footerButtons,
  header,
  headerDividerFullBleed,
} from '../billPayFlyoutChrome.style'

export default css`
  ${flyoutSurface('PayBillFlyout')}

  &.PayBillFlyout .BaseFlyout-Container {
    /* The preview shelf is an absolutely-positioned sibling that slides out
       from behind the primary pane. React Modal defaults this content box to
       overflow: auto, which clips that left-side motion to the pane's 640px
       bounds. Scrolling remains owned by the form and preview bodies. */
    /* !important is required because React Modal supplies overflow: auto as
       an inline style on this element. */
    overflow: visible !important;
  }

  &.PayBillFlyout .BaseFlyout-Content {
    width: min(640px, 100vw);

    &.secondaryFlyout {
      width: min(460px, max(0px, calc(100vw - min(640px, 100vw))));

      &:not(.showSecondaryContent) .BaseFlyout-Body {
        display: none;
      }

      &.showSecondaryContent {
        right: min(640px, 100vw);
      }

      .BaseFlyout-CloseBar {
        padding: ${spacing(6)} ${spacing(6)} 0;
        margin-bottom: 0;
      }

      .BaseFlyout-Header {
        gap: ${spacing(1)};
        padding: ${spacing(3)} ${spacing(6)} ${spacing(4)};

        h2 {
          margin: 0;
          ${fontBold};
        }

        p {
          margin: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          font-family: ${font_family_weight_regular};
          ${font_sm};
          color: ${text_colorTextSecondary};
        }
      }

      .BaseFlyout-Body {
        padding: ${spacing(6)};
        overflow: hidden;
      }
    }
  }
`

/* The step body fills the remaining flyout height: scrollable field list + a
   pinned footer that stays put while the fields scroll. */
export const stepBody = css`
  display: flex;
  flex-direction: column;
  flex: auto;
  min-height: 0;

  &:focus {
    outline: none;
  }
`

/**
 * Trailing space below the last field, on top of the list's own padding.
 *
 * Both date fields live in the final section and their calendars open downward
 * and stay there (`pinPlacement` in PayBillFlyout.tsx). DateInput makes room by
 * scrolling this list, so the room has to exist: a calendar is 342px tall plus
 * 22px of popper offset, and at the end of the scroll the date row is only as
 * high as whatever follows it can push it. Memo alone left the calendar
 * overhanging the window by 33px with nothing left to scroll.
 *
 * Sized for a ~900px window, the shortest this app is used at. It buys the last
 * ~40px of slack rather than the whole calendar — the memo field and the gap
 * above it do most of the work.
 */
const calendarClearance = spacing(45)

export const fields = css`
  flex: auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing(6)};
  padding: ${spacing(6)} ${paddingHorizontal} ${calendarClearance};
`

/*
 * Titled sections + hairline dividers, mirroring the prototype's
 * `FlyoutSection` / `FlyoutDivider`. This is the same "white surface, grouping
 * by dividers" direction as the flyout body above: hierarchy comes from a
 * tight title→fields cluster and a rule between sections, not from cards.
 *
 * Declared prototype values: title 20px / 24px line-height / medium
 * (= font_2xl + font_family_weight_medium), 20px title→fields, 24px between
 * fields, and a full-width 1px rule in the PRIMARY content-border token.
 */
export const section = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(5)};
`

export const sectionTitle = css`
  margin: 0;
  ${font_2xl}
  font-family: ${font_family_weight_medium};
  /* Explicit 500 so the browser doesn't layer a synthetic bold over
     Rubik-Medium's own weight. */
  font-weight: 500;
  color: ${text_colorTextPrimary};
`

export const sectionFields = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(6)};
`

export const sectionDivider = css`
  height: 1px;
  width: 100%;
  flex-shrink: 0;
  background-color: ${base_colorContentBorder};
`

/* TextAreaInput's textarea sets an 80px min-height of its own, which outranks
   `rows` and renders four lines. Releasing it lets `rows={3}` decide. */
export const memoField = css`
  textarea {
    min-height: 0;
  }
`

/* Invoice Date + Due Date sit side by side in a 2-column grid with a 16px gap. */
export const datesRow = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing(4)};
`
